import { useEffect, useRef, useState, useCallback } from 'react';

const WS_BASE = process.env.REACT_APP_BACKEND_URL
  ?.replace('https://', 'wss://')
  ?.replace('http://', 'ws://') || '';

export function useWebSocket(userId, token) {
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const reconnectRef = useRef(0);
  const maxReconnect = 5;
  const mountedRef = useRef(true);

  const connect = useCallback(() => {
    if (!userId || !token || !mountedRef.current) return;

    try {
      const url = `${WS_BASE}/ws/${userId}?token=${token}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) return;
        setConnected(true);
        reconnectRef.current = 0;
      };

      ws.onmessage = (event) => {
        if (!mountedRef.current) return;
        try {
          const data = JSON.parse(event.data);
          if (data.type !== 'pong') {
            setLastMessage(data);
          }
        } catch {}
      };

      ws.onclose = () => {
        if (!mountedRef.current) return;
        setConnected(false);
        wsRef.current = null;
        if (reconnectRef.current < maxReconnect && mountedRef.current) {
          const delay = Math.min(1000 * Math.pow(2, reconnectRef.current), 30000);
          reconnectRef.current++;
          setTimeout(() => { if (mountedRef.current) connect(); }, delay);
        }
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch {}
  }, [userId, token]);

  useEffect(() => {
    mountedRef.current = true;
    connect();

    // Keepalive ping every 25s
    const ping = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 25000);

    return () => {
      mountedRef.current = false;
      clearInterval(ping);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  return { connected, lastMessage };
}
